import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useScrollAnimation, useScrollAnimationMultiple } from "@/hooks/useScrollAnimation";

const menuConfig = [
  {
    icon: "ri-user-line",
    tabKey: "services_tab1",
    tabEnKey: "services_tab1_en",
    descKey: "services_tab1_desc",
    color: "bg-[#e8f9f8]",
    iconColor: "text-[#0ABAB5]",
    rows: [
      { targetKey: "services_row1_target1", price: "22,000円" },
      { targetKey: "services_row1_target2", price: "33,000円" },
      { targetKey: "services_row1_target3", price: "44,000円" },
    ],
  },
  {
    icon: "ri-briefcase-line",
    tabKey: "services_tab2",
    tabEnKey: "services_tab2_en",
    descKey: "services_tab2_desc",
    color: "bg-[#fff8f0]",
    iconColor: "text-[#e8a87c]",
    rows: [
      { targetKey: "services_row2_target1", price: "44,000円" },
    ],
  },
  {
    icon: "ri-heart-2-line",
    tabKey: "services_tab3",
    tabEnKey: "services_tab3_en",
    descKey: "services_tab3_desc",
    color: "bg-[#fdf0f5]",
    iconColor: "text-[#e87ca0]",
    rows: [
      { targetKey: "services_row3_target1", price: "11,000円" },
    ],
  },
];

const includeKeys = [
  "services_inc1",
  "services_inc2",
  "services_inc3",
  "services_inc4",
  "services_inc5",
];

export default function ServicesSection() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: tabRef, isVisible: tabVisible } = useScrollAnimation();
  const { ref: cardRef, isVisible: cardVisible } = useScrollAnimation();
  const { ref: includesRef, isVisible: includesVisible } = useScrollAnimation();
  const { setRef: setBottomRef, visibleItems: bottomVisible } = useScrollAnimationMultiple(2);

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" className="py-16 sm:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-10 sm:mb-14 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-5"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            {t("services_heading")}
          </h2>
          <div className="inline-block bg-[#e8f9f8] border border-[#0ABAB5]/30 rounded-xl px-4 sm:px-6 py-3 mt-2">
            <p className="text-[#0ABAB5] text-xs leading-relaxed">
              {t("services_note1")}<br />
              {t("services_note2")}
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div
          ref={tabRef as React.RefObject<HTMLDivElement>}
          className={`mb-8 sm:mb-10 transition-all duration-700 ease-out ${tabVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          style={{ transitionDelay: tabVisible ? "100ms" : "0ms" }}
        >
          {/* Mobile: 縦並びタブ */}
          <div className="flex flex-col gap-2 sm:hidden">
            {menuConfig.map((item, i) => (
              <button
                key={item.tabKey}
                onClick={() => setActiveTab(i)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer border ${
                  activeTab === i
                    ? "bg-[#0ABAB5] text-white border-[#0ABAB5]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-[#0ABAB5]/40"
                }`}
              >
                <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${activeTab === i ? "bg-white/20" : item.color}`}>
                  <i className={`${item.icon} text-base ${activeTab === i ? "text-white" : item.iconColor}`} />
                </div>
                <span className="text-left">{t(item.tabKey)}</span>
                {activeTab === i && <i className="ri-check-line ml-auto text-white" />}
              </button>
            ))}
          </div>

          {/* Desktop: 横並びタブ（既存デザイン） */}
          <div className="hidden sm:flex justify-center">
            <div className="flex bg-white border border-gray-200 rounded-full p-1 gap-1">
              {menuConfig.map((item, i) => (
                <button
                  key={item.tabKey}
                  onClick={() => setActiveTab(i)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 ${
                    activeTab === i
                      ? "bg-[#0ABAB5] text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <i className={`${item.icon} mr-1`} />
                  {t(item.tabKey)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active Card */}
        <div
          ref={cardRef as React.RefObject<HTMLDivElement>}
          className={`transition-all duration-700 ease-out ${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: cardVisible ? "200ms" : "0ms" }}
        >
          {menuConfig.map((item, i) => (
            <div
              key={item.tabKey}
              className={`transition-all duration-300 ${activeTab === i ? "block" : "hidden"}`}
            >
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                {/* Top banner */}
                <div className={`${item.color} px-5 md:px-8 py-6 md:py-8 flex items-start gap-4 md:gap-5`}>
                  <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-white/80 flex-shrink-0">
                    <i className={`${item.icon} ${item.iconColor} text-2xl md:text-3xl`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium tracking-widest mb-1">{t(item.tabEnKey)}</p>
                    <h3
                      className="text-xl md:text-2xl font-bold text-gray-800"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    >
                      {t(item.tabKey)}
                    </h3>
                    <p className="text-gray-500 text-sm mt-1.5 leading-relaxed">{t(item.descKey)}</p>
                  </div>
                </div>

                {/* Price table */}
                <div className="px-5 md:px-8 py-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left text-xs text-gray-400 font-medium pb-3 w-2/3">{t("services_col_target")}</th>
                        <th className="text-right text-xs text-gray-400 font-medium pb-3">{t("services_col_price")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.rows.map((row, ri) => (
                        <tr
                          key={ri}
                          className={ri < item.rows.length - 1 ? "border-b border-gray-50" : ""}
                        >
                          <td className="py-4 text-gray-700 font-medium text-xs md:text-sm">{t(row.targetKey)}</td>
                          <td className="py-4 text-right">
                            <span className="text-lg md:text-xl font-bold text-[#0ABAB5]">{row.price}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* CTA */}
                <div className="px-5 md:px-8 pb-6 md:pb-8">
                  <button
                    onClick={() => handleNav("#contact")}
                    className="w-full bg-[#0ABAB5] text-white text-sm font-semibold py-3.5 rounded-full hover:bg-[#089e99] transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {t("services_cta")}
                    <i className="ri-arrow-right-line ml-2" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Includes */}
        <div
          ref={includesRef as React.RefObject<HTMLDivElement>}
          className={`mt-12 bg-white rounded-2xl border border-gray-100 px-5 md:px-8 py-6 md:py-8 transition-all duration-700 ease-out ${includesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: includesVisible ? "100ms" : "0ms" }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#e8f9f8]">
              <i className="ri-file-list-3-line text-[#0ABAB5] text-base" />
            </div>
            <h4
              className="text-lg font-bold text-gray-800"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              {t("services_includes_title")}
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {includeKeys.map((key) => (
              <div key={key} className="flex items-start gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-[#0ABAB5] text-base" />
                </div>
                <span className="text-sm text-gray-600 leading-relaxed">{t(key)}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-5 leading-relaxed border-t border-gray-50 pt-5">
            {t("services_includes_note")}
          </p>
        </div>

        {/* Payment & First-timer */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Payment */}
          <div
            ref={setBottomRef(0) as unknown as React.RefObject<HTMLDivElement>}
            className={`bg-white rounded-2xl border border-gray-100 px-7 py-7 transition-all duration-700 ease-out ${bottomVisible[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#fff8f0]">
                <i className="ri-bank-line text-[#e8a87c] text-base" />
              </div>
              <h4
                className="text-base font-bold text-gray-800"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {t("services_payment_title")}
              </h4>
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-2">{t("services_payment_method")}</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              {t("services_payment_desc").split("\n").map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </p>
          </div>

          {/* First-timer */}
          <div
            ref={setBottomRef(1) as unknown as React.RefObject<HTMLDivElement>}
            className={`bg-[#e8f9f8] rounded-2xl border border-[#0ABAB5]/20 px-7 py-7 transition-all duration-700 ease-out ${bottomVisible[1] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            style={{ transitionDelay: bottomVisible[1] ? "150ms" : "0ms" }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/70">
                <i className="ri-seedling-line text-[#0ABAB5] text-base" />
              </div>
              <h4
                className="text-base font-bold text-gray-800"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {t("services_first_title")}
              </h4>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              {t("services_first_desc").split("\n").map((line, i, arr) => (
                <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
              ))}
            </p>
            <button
              onClick={() => handleNav("#contact")}
              className="mt-4 text-xs font-semibold text-[#0ABAB5] border border-[#0ABAB5] px-4 py-2 rounded-full hover:bg-[#0ABAB5] hover:text-white transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {t("services_first_cta")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
